import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button, Modal } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Check, ExternalLink, ExternalLinkIcon, MousePointer2, RefreshCcw, RefreshCw, Zap } from 'lucide-react';
import { EXTENSION_URL, checkExtensionAvailability } from 'services/chromeExtension';

export const RefreshContactModal = ({ onCloseModal, linkedinUrl, trackRefreshEvent }) => {
  const [isExtensionAvailable, setExtensionAvailability] = useState(false);
  const hasLinkedinUrl = Boolean(linkedinUrl);
  const normalizedLinkedinUrl = linkedinUrl?.endsWith('/') ? linkedinUrl : `${linkedinUrl}/`;

  useEffect(() => {
    if (hasLinkedinUrl) {
      checkExtensionAvailability().then((value) => {
        setExtensionAvailability(value);
      });
    }
  }, [hasLinkedinUrl]);
  const [checked, setChecked] = useState([]);

  const installOptions = [
    {
      icon: Zap,
      text: 'Sync your LinkedIn connections automatically',
    },
    {
      icon: MousePointer2,
      text: 'Add and manage Contacts directly from LinkedIn',
    },
    {
      icon: RefreshCcw,
      text: 'Refresh any profile, anytime',
    },
  ];

  const renderStepIndicator = (option) => {
    if (option.isSpinner) {
      return (
        <Flex
          data-testid={`${option.id}-spinner`}
          sx={{
            backgroundColor: 'purple.400',
            borderRadius: '40px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          <Box
            as={RefreshCw}
            size="16px"
            aria-hidden="true"
            sx={{
              color: 'neutral.0',
              animation: 'spin 2s linear infinite',
            }}
          />
        </Flex>
      );
    }
    if (checked.includes(option.id)) {
      return (
        <Flex
          data-testid={`${option.id}-checked`}
          sx={{
            backgroundColor: 'purple.400',
            borderRadius: '40px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          <Box as={Check} size="16px" sx={{ color: 'neutral.0' }} />
        </Flex>
      );
    }
    return (
      <Flex
        sx={{
          color: 'text.main',
          fontSize: '14px',
          fontWeight: 500,
          width: '24px',
          height: '24px',
          backgroundColor: 'neutral.100',
          borderRadius: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {option.id}
      </Flex>
    );
  };

  const refreshOptions = [
    {
      id: 1,
      text: (
        <Flex
          onClick={() => {
            if (!checked.includes(1)) {
              setChecked([...checked, 1]);
              trackRefreshEvent();
            }
          }}
          sx={{ alignItems: 'center', gap: '8px' }}
        >
          <Box target="__blank" as="a" href={normalizedLinkedinUrl}>
            Open their LinkedIn profile
          </Box>
          <Box as={ExternalLinkIcon} size={16} />
        </Flex>
      ),
    },
    {
      id: 2,
      text: (
        <Flex
          onClick={() => {
            if (!checked.includes(2)) {
              setChecked([...checked, 2]);
              trackRefreshEvent();
            }
          }}
          sx={{ alignItems: 'center', gap: '8px' }}
        >
          <Box target="__blank" as="a" href={`${normalizedLinkedinUrl}details/experience/`}>
            Open their experience details
          </Box>
          <Box as={ExternalLinkIcon} size={16} />
        </Flex>
      ),
    },
    {
      id: 3,
      isSpinner: checked.includes(1) && checked.includes(2),
      text: (
        <Box>
          {checked.includes(1) && checked.includes(2)
            ? 'New info coming... Close and reopen the profile shortly to see the updates.'
            : 'After opening both, the profile updates shortly'}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ cursor: 'default' }} data-testid="refresh-contact-modal" onClick={(e) => e.stopPropagation()}>
      {createPortal(
        <Modal title="Refresh contact’s profile" onCancel={onCloseModal}>
          <Flex sx={{ flexDirection: 'column', gap: '24px', fontSize: '14px' }}>
            <Text>
              Getro refreshes profiles automatically, but updates can take a few days — and some profiles (usually
              private or with an invalid link) can’t be enriched at all.
            </Text>
            {hasLinkedinUrl && (
              <Flex sx={{ flexDirection: 'column', gap: '16px', bg: 'neutral.20', p: '16px', borderRadius: '8px' }}>
                <Text fontWeight="semibold" color="text.dark">
                  {isExtensionAvailable
                    ? 'Refresh the profile in 2 quick steps:'
                    : 'Install the Getro extension to refresh profiles'}
                </Text>
                <Box sx={{ flexDirection: 'column', gap: '16px', display: 'flex' }}>
                  {!isExtensionAvailable && (
                    <>
                      <Text>It will automatically refresh every LinkedIn profile you visit, plus:</Text>
                      {installOptions.map(({ icon, text }) => (
                        <Flex key={text} sx={{ gap: '8px', alignItems: 'center' }}>
                          <Box as={icon} sx={{ color: 'purple.300', size: 20 }} />
                          <Box>{text}</Box>
                        </Flex>
                      ))}
                    </>
                  )}

                  {isExtensionAvailable && (
                    <>
                      <Box
                        sx={{
                          gap: '8px',
                          alignItems: 'center',
                          flexDirection: 'column',
                          '@keyframes spin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                          },
                        }}
                      >
                        <Box>
                          {refreshOptions.map((option, index) => (
                            <Flex key={option.id} sx={{ gap: '8px', alignItems: 'flex-start' }}>
                              <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
                                {renderStepIndicator(option)}
                                {index !== refreshOptions.length - 1 && (
                                  <Box
                                    sx={{
                                      width: '2px',
                                      height: '32px',
                                      backgroundColor: 'neutral.100',
                                    }}
                                  />
                                )}
                              </Flex>
                              <Box sx={{ flex: 1, lineHeight: '24px' }}>{option.text}</Box>
                            </Flex>
                          ))}
                        </Box>
                      </Box>
                      <Text>No need to open the extension — just let the page load.</Text>
                    </>
                  )}
                </Box>
              </Flex>
            )}
            {hasLinkedinUrl && !isExtensionAvailable && (
              <Button
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                }}
                as="a"
                type="link"
                data-testid="submit-btn"
                icon={ExternalLink}
                iconPosition="right"
                target="__blank"
                iconGap="8px"
                href={EXTENSION_URL}
              >
                Install extension
              </Button>
            )}
          </Flex>
        </Modal>,
        document.getElementById('portals'),
      )}
    </Box>
  );
};

RefreshContactModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  trackRefreshEvent: PropTypes.func.isRequired,
  linkedinUrl: PropTypes.string,
};

RefreshContactModal.defaultProps = {
  linkedinUrl: null,
};
